import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Brain, Coins, Sparkles, Trophy, Volume2, VolumeX, Info, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import RiddleCard from '../components/RiddleCard';

const RiddlePage = () => {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [riddles, setRiddles] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [totalCoins, setTotalCoins] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showFinalCelebration, setShowFinalCelebration] = useState(false);

  useEffect(() => {
    fetchRiddles();
  }, []);

  const fetchRiddles = async () => {
    try {
      const response = await api.get('/riddles/daily');
      setRiddles(response.data.riddles);
      setCompleted(response.data.completed);
      setTotalCoins(response.data.coins_earned || 0);

      const existingResults = {};
      let lastAnsweredIndex = -1;
      Object.entries(response.data.progress || {}).forEach(([riddleId, data]) => {
        const riddle = response.data.riddles.find(r => r.id === riddleId);
        if (data.correct || data.attempts >= 3) {
          existingResults[riddleId] = {
            correct: data.correct,
            correct_answer: riddle?.correct_answer,
            coins_earned: data.coins_earned
          };
          const idx = response.data.riddles.findIndex(r => r.id === riddleId);
          if (idx > lastAnsweredIndex) lastAnsweredIndex = idx;
        }
      });
      setResults(existingResults);

      if (response.data.completed) {
        setCurrentIndex(response.data.riddles.length - 1);
      } else if (lastAnsweredIndex >= 0) {
        setCurrentIndex(Math.min(lastAnsweredIndex + 1, response.data.riddles.length - 1));
      }
    } catch (error) {
      toast.error('Could not load riddles');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (riddleId, answer) => {
    try {
      const response = await api.post('/riddles/answer', {
        riddle_id: riddleId,
        answer: answer
      });
      const result = response.data;

      if (result.correct) {
        setResults(prev => ({
          ...prev,
          [riddleId]: {
            correct: true,
            correct_answer: result.correct_answer,
            coins_earned: result.coins_earned,
            fun_fact: result.fun_fact
          }
        }));
      }

      setTotalCoins(result.total_coins);

      if (result.all_completed) {
        setCompleted(true);
        await refreshUser();
        if (result.streak_bonus > 0) {
          toast.success(`Streak bonus: +${result.streak_bonus} coins!`, { duration: 4000 });
        }
      }

      if (result.max_attempts_reached && !result.correct) {
        setResults(prev => ({
          ...prev,
          [riddleId]: {
            correct: false,
            correct_answer: result.correct_answer,
            coins_earned: 0,
            maxedOut: true
          }
        }));
      }

      return result;
    } catch (error) {
      toast.error('Could not submit answer');
    }
  };

  const handleNextRiddle = useCallback(() => {
    if (currentIndex < riddles.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setShowFinalCelebration(true);
    }
  }, [currentIndex, riddles.length]);

  const correctCount = Object.values(results).filter(r => r?.correct).length;
  const answeredCount = Object.keys(results).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce mb-4">🧩</div>
          <p className="text-xl text-slate-600 font-fredoka">Loading Riddles...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="riddle-page" className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100">
      {/* Final celebration overlay */}
      {showFinalCelebration && (
        <div data-testid="final-celebration" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in">
          <div className="bg-white rounded-3xl p-8 max-w-sm mx-4 text-center animate-pop shadow-2xl border-4 border-yellow-400">
            <div className="text-6xl mb-4">
              {correctCount === 3 ? '🏆' : correctCount >= 2 ? '⭐' : '👍'}
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-2 font-fredoka">
              {correctCount === 3 ? 'Perfect Score!' : correctCount >= 2 ? 'Great Job!' : 'Nice Try!'}
            </h2>
            <p className="text-slate-600 mb-4">
              You got {correctCount} out of 3 riddles correct!
            </p>
            <div className="flex items-center justify-center gap-2 text-2xl font-bold text-amber-600 mb-6">
              <span className="text-3xl">🪙</span>
              <span>+{totalCoins} coins earned!</span>
            </div>
            <button
              data-testid="celebration-back-btn"
              onClick={() => navigate('/dashboard')}
              className="px-8 py-4 bg-gradient-to-r from-primary to-amber-400 text-black rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>

          <h1 className="text-lg font-bold text-slate-900 font-fredoka flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            Daily Riddles
          </h1>

          <div className="flex items-center gap-2">
            <button
              data-testid="sound-toggle"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-purple-500" />
              ) : (
                <VolumeX className="w-5 h-5 text-slate-400" />
              )}
            </button>
            <div className="flex items-center gap-1 px-3 py-1 bg-amber-100 rounded-full">
              <Coins className="w-4 h-4 text-amber-600" />
              <span data-testid="total-coins" className="font-bold text-sm text-amber-700">{totalCoins}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Progress Steps — clean pill bar */}
        <div data-testid="progress-steps" className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            {riddles.map((riddle, i) => {
              const isAnswered = !!results[riddle.id];
              const isCorrect = results[riddle.id]?.correct;
              const isCurrent = i === currentIndex;

              return (
                <div key={riddle.id} className="flex-1 flex items-center gap-2">
                  <div
                    data-testid={`progress-step-${i}`}
                    className={`
                      flex-1 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-300
                      ${isCurrent && !isAnswered
                        ? 'bg-purple-600 text-white shadow-md shadow-purple-200'
                        : isAnswered
                          ? isCorrect
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-300 text-white'
                          : 'bg-slate-100 text-slate-400'
                      }
                    `}
                  >
                    {isAnswered ? (isCorrect ? '✓' : '✗') : `Riddle ${i + 1}`}
                  </div>
                  {i < riddles.length - 1 && (
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isAnswered ? 'text-slate-400' : 'text-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Riddle — key forces fresh component on index change */}
        {!completed ? (
          <div data-testid="current-riddle" key={`riddle-wrapper-${currentIndex}`} className="animate-slide-up">
            <RiddleCard
              key={riddles[currentIndex]?.id}
              riddle={riddles[currentIndex]}
              index={currentIndex}
              onAnswer={handleAnswer}
              answered={!!results[riddles[currentIndex]?.id]}
              result={results[riddles[currentIndex]?.id]}
              onNextRiddle={handleNextRiddle}
              isLast={currentIndex === riddles.length - 1}
              soundEnabled={soundEnabled}
            />
          </div>
        ) : (
          /* Completed state */
          <div data-testid="completed-banner" className="card-bubbly bg-gradient-to-r from-amber-100 to-yellow-100 text-center">
            <Trophy className="w-16 h-16 text-amber-600 mx-auto mb-3" />
            <h3 className="text-2xl font-black text-slate-900 mb-2 font-fredoka">All Done for Today!</h3>
            <p className="text-slate-600 mb-2">
              You answered {correctCount}/3 correctly and earned {totalCoins} coins!
            </p>
            <p className="text-slate-500 text-sm mb-6">Come back tomorrow for new riddles!</p>
            <button
              data-testid="completed-back-btn"
              onClick={() => navigate('/dashboard')}
              className="px-8 py-4 bg-gradient-to-r from-primary to-amber-400 text-black rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {/* Reward System Explanation */}
        <div data-testid="reward-explanation" className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-purple-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-purple-500" />
            <h4 className="font-bold text-slate-800">How Riddle Rewards Work</h4>
          </div>
          <div className="space-y-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span className="text-lg">🪙</span>
              <span><strong>5 coins</strong> for solving a riddle on first try</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🪙</span>
              <span><strong>3 coins</strong> for solving after using a hint</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🔄</span>
              <span>You get <strong>3 attempts</strong> per riddle — use hints wisely!</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🏆</span>
              <span><strong>+10 bonus</strong> for a perfect score (3/3 correct)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">⭐</span>
              <span><strong>+5 bonus</strong> for getting at least 2/3 correct</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RiddlePage;
